// Angular
import { Injectable, inject, signal, computed } from '@angular/core';
// Constants
import { RECENT_SEARCHES_LS_KEY } from '@constants';
// Service
import { CommonService } from './common.service';

interface ISavedSearch {
    url: string;
    title: string;
    total: number;
    timestamp: string;
}

@Injectable({
    providedIn: 'root',
})
export class SavedSearchesService {

    // Private
    private commonService = inject(CommonService);

    // Public
    public savedSearchesSignal = signal<ISavedSearch[]>(this.getInitialSavedSearches());
    public savedSearchesCountSignal = computed(() => this.savedSearchesSignal().length);

    /**
     * Initial data from local storage.
     * @return {*}  {ISavedSearch[]}
     * @memberof SavedSearchesService
     */
    private getInitialSavedSearches(): ISavedSearch[] {
        const data = this.commonService.getFromLocalStorage(RECENT_SEARCHES_LS_KEY);
        if (!Array.isArray(data)) return [];
        return data.map((item) => ({
            ...item,
            timestamp: typeof item.timestamp === 'number' ? new Date(item.timestamp).toISOString() : String(item.timestamp)
        }));
    }

    /**
     * Add a new search to the list
     * @param {ISavedSearch} searchItem
     * @memberof SavedSearchesService
     */
    addSearch(searchItem: ISavedSearch): void {
        this.savedSearchesSignal.update((currentSearches) => {
            const filtered = currentSearches.filter((item) => item.url !== searchItem.url);
            const updated = [searchItem, ...filtered].slice(0, 150);
            this.commonService.setIntoLocalStorage(RECENT_SEARCHES_LS_KEY, updated);
            return updated;
        });
    }

    /**
     * Remove a search by its URL.
     * @param {string} url
     * @memberof SavedSearchesService
     */
    removeSearch(url: string): void {
        this.savedSearchesSignal.update((currentSearches) => {
            const updated = currentSearches.filter((item) => item.url !== url);
            this.commonService.setIntoLocalStorage(RECENT_SEARCHES_LS_KEY, updated);
            return updated;
        });
    }

    /**
     * Check if a specific URL is already saved.
     * @param {string} url
     * @memberof SavedSearchesService
     */
    isSearchSaved(url: string): boolean {
        return this.savedSearchesSignal().some((item) => item.url === url);
    }

    /**
     * Clear all saved searches.
     * @memberof SavedSearchesService
     */
    clearAll(): void {
        this.savedSearchesSignal.set([]);
        this.commonService.removeFromLocalStorage(RECENT_SEARCHES_LS_KEY);
    }
}